import React, { useState, useEffect, useCallback } from 'react';
import {
  Form,
  Input,
  Select,
  Button,
  Table,
  InputNumber,
  Space,
  Card,
  Row,
  Col,
  message,
  Typography,
  DatePicker,
  Radio,
  Checkbox,
  AutoComplete
} from 'antd';
import { PlusOutlined, DeleteOutlined, SaveOutlined, SyncOutlined, CheckOutlined, CloseOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { 
  createInvoice, 
  updateInvoice,
  INVOICE_TYPES,
  INVOICE_TYPE_LABELS,
  getAvailableRentalInvoices
} from '../../firebase/invoiceService';
import { generateInvoicePDF } from './InvoicePDF';
import AdditionalServicesSection from '../Shared/AdditionalServicesSection';
import DiscountSection from '../Shared/DiscountSection';
import GSTSection from '../Shared/GSTSection';
import UnifiedDateSection from '../Shared/UnifiedDateSection';
import { getDatesForPDF } from '../../utils/unifiedDateStorage';
import { createEmptyDateEntry } from '../../utils/newDateUtils';
import { DEFAULT_ADDITIONAL_SERVICES, DEFAULT_DISCOUNT, DEFAULT_GST, PAYMENT_TERMS, mapPaymentTermsForForm } from '../Shared/constants';
import moment from 'moment';
import { convertDateToAntdFormat, convertAntdDateToStorage } from '../../utils/dateUtils';
import {
  createEnhancedLineItem,
  updateLineItemWithSmartDescription,
  resetToAutoDescription,
  regenerateAutoDescriptionsOnLoad
} from '../../utils/descriptionGenerator';
import '../Form/AdminFormMobile.css';

const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;
const {  } = DatePicker;

// Individual Mobile Line Item Component with stable keys and controlled inputs
const MobileLineItem = React.memo(({ item, index, onUpdate, onRemove, canRemove, onResetDescription, onAcceptUpdate, onDismissUpdate }) => {
  const handleDescriptionChange = React.useCallback((e) => {
    onUpdate(item.id, 'description', e.target.value);
  }, [item.id, onUpdate]);

  const handleQuantityChange = React.useCallback((value) => {
    onUpdate(item.id, 'quantity', value);
  }, [item.id, onUpdate]);

  const handleUnitPriceChange = React.useCallback((value) => {
    onUpdate(item.id, 'unitPrice', value);
  }, [item.id, onUpdate]);

  const handleRemove = React.useCallback(() => {
    onRemove(item.id);
  }, [item.id, onRemove]);

  return (
    <div className="mobile-line-item">
      <div className="mobile-line-item-header">
        <span>Item #{index + 1}</span>
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={handleRemove}
          disabled={!canRemove}
          size="small"
        />
      </div>
      
      <div className="mobile-line-item-field">
        <label>Description</label>
        <div style={{ position: 'relative' }}>
          <TextArea
            key={`desc-${item.id}`}
            value={item.description || ''}
            onChange={handleDescriptionChange}
            placeholder="Enter service description (multi-line supported)"
            autoSize={{ minRows: 2, maxRows: 4 }}
            style={{
              borderColor: item.isManuallyEdited ? '#1890ff' : undefined,
              backgroundColor: item.isManuallyEdited ? '#f6ffed' : undefined
            }}
          />
          {/* Visual indicator for auto-generated vs manual descriptions */}
          {!item.isManuallyEdited && item.autoDescription && (
            <div style={{
              position: 'absolute',
              top: 4,
              right: 4,
              fontSize: '10px',
              color: '#52c41a',
              backgroundColor: 'rgba(82, 196, 26, 0.1)',
              padding: '2px 4px',
              borderRadius: '2px',
              display: 'flex',
              alignItems: 'center',
              gap: '2px'
            }}>
              <SyncOutlined style={{ fontSize: '8px' }} />
              Auto
            </div>
          )}
        </div>

        {/* Update prompt for manually edited descriptions */}
        {item.showUpdatePrompt && (
          <div style={{
            marginTop: 4,
            padding: '6px 8px',
            backgroundColor: '#fff7e6',
            border: '1px solid #ffd591',
            borderRadius: '4px',
            fontSize: '12px'
          }}>
            <div style={{ marginBottom: 4 }}>
              <InfoCircleOutlined style={{ color: '#fa8c16', marginRight: 4 }} />
              <span>Update available:</span>
            </div>
            <div style={{ 
              padding: '4px 6px', 
              backgroundColor: '#f0f0f0', 
              borderRadius: '2px',
              fontSize: '11px',
              marginBottom: 4
            }}>
              "{item.autoDescription}"
            </div>
            <div style={{ display: 'flex', gap: '4px' }}>
              <Button
                size="small"
                type="primary"
                icon={<CheckOutlined />}
                onClick={() => onAcceptUpdate(item.id)}
                style={{ fontSize: '10px', height: '20px' }}
              >
                Accept
              </Button>
              <Button
                size="small"
                icon={<CloseOutlined />}
                onClick={() => onDismissUpdate(item.id)}
                style={{ fontSize: '10px', height: '20px' }}
              >
                Dismiss
              </Button>
            </div>
          </div>
        )}

        {/* Helper function controls */}
        <div style={{ marginTop: 8, padding: '8px', backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <div>
              <Radio.Group
                size="small"
                value={item.width}
                onChange={(e) => onUpdate(item.id, 'width', e.target.value)}
              >
                <Radio.Button value="single">Single</Radio.Button>
                <Radio.Button value="double">Double</Radio.Button>
              </Radio.Group>
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <InputNumber
                size="small"
                placeholder="Working Height"
                value={item.workingHeight}
                onChange={(value) => onUpdate(item.id, 'workingHeight', value)}
                min={2}
                max={15}
                step={0.1}
                addonAfter="m"
                style={{ flex: 1 }}
              />
              {/* Reset to auto button */}
              {item.isManuallyEdited && item.autoDescription && (
                <Button
                  size="small"
                  type="text"
                  icon={<SyncOutlined />}
                  onClick={() => onResetDescription(item.id)}
                  title="Reset to auto-generated description"
                  style={{ color: '#1890ff', fontSize: '10px' }}
                >
                  Reset
                </Button>
              )}
            </div>
            {/* Service checkboxes */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <Checkbox
                size="small"
                checked={item.services?.transportation || false}
                onChange={(e) => onUpdate(item.id, 'services', { 
                  ...item.services, 
                  transportation: e.target.checked 
                })}
                style={{ fontSize: '11px' }}
              >
                Transportation
              </Checkbox>
              <Checkbox
                size="small"
                checked={item.services?.assembly || false}
                onChange={(e) => onUpdate(item.id, 'services', { 
                  ...item.services, 
                  assembly: e.target.checked 
                })}
                style={{ fontSize: '11px' }}
              >
                Assembly and dismantling
              </Checkbox>
              <Checkbox
                size="small"
                checked={item.services?.endorsement || false}
                onChange={(e) => onUpdate(item.id, 'services', { 
                  ...item.services, 
                  endorsement: e.target.checked 
                })}
                style={{ fontSize: '11px' }}
              >
                Weekly inspection
              </Checkbox>
            </div>
          </Space>
        </div>
      </div>
      
      <div className="mobile-line-item-field">
        <label>Quantity</label>
        <InputNumber
          key={`qty-${item.id}`}
          min={1}
          value={item.quantity}
          onChange={handleQuantityChange}
          style={{ width: '100%' }}
        />
      </div>
      
      <div className="mobile-line-item-field">
        <label>Unit Price (SGD)</label>
        <InputNumber
          key={`price-${item.id}`}
          min={0}
          precision={2}
          value={item.unitPrice}
          onChange={handleUnitPriceChange}
          formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
          style={{ width: '100%' }}
        />
      </div>
      
      <div className="mobile-line-item-field">
        <label>Total</label>
        <div style={{ 
          padding: '8px 12px', 
          backgroundColor: '#f5f5f5', 
          borderRadius: '6px',
          fontWeight: 'bold',
          color: '#1890ff'
        }}>
          ${(item.total || 0).toFixed(2)}
        </div>
      </div>
    </div>
  );
});

// Mobile Line Items Container Component with stable rendering
const MobileLineItems = React.memo(({ lineItems, onUpdate, onRemove, onResetDescription, onAcceptUpdate, onDismissUpdate }) => {
  return (
    <div className="mobile-line-items">
      {lineItems.map((item, index) => (
        <MobileLineItem
          key={item.id}
          item={item}
          index={index}
          onUpdate={onUpdate}
          onRemove={onRemove}
          canRemove={lineItems.length > 1}
          onResetDescription={onResetDescription}
          onAcceptUpdate={onAcceptUpdate}
          onDismissUpdate={onDismissUpdate}
        />
      ))}
    </div>
  );
});

// Helper function to map customer info from quotation
const mapCustomerInfoFromQuotation = (quotation) => {
  return {
    companyName: quotation.companyName || '',
    attn: quotation.attn || '',
    tel: quotation.tel || '',
    email: quotation.email || '',
    billingAddress: quotation.billingAddress || '',
    siteAddress: quotation.siteAddress || ''
  };
};

const InvoiceForm = ({ 
  invoice = null, 
  onSave = () => {},
  onCancel = () => {},
  linkedQuotation = null
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [lineItems, setLineItems] = useState([
    createEnhancedLineItem({ 
      id: 1, 
      description: '', 
      quantity: 1, 
      unitPrice: 0, 
      total: 0,
      workingHeight: undefined,
      width: undefined
    })
  ]);
  const [additionalServices, setAdditionalServices] = useState(DEFAULT_ADDITIONAL_SERVICES);
  const [invoiceType, setInvoiceType] = useState(INVOICE_TYPES.PURCHASE);
  const [formKey, setFormKey] = useState(Date.now());
  const [discount, setDiscount] = useState(DEFAULT_DISCOUNT);
  const [gst, setGst] = useState(DEFAULT_GST);
  const [customAdditionalServices, setCustomAdditionalServices] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [availableRentalInvoices, setAvailableRentalInvoices] = useState([]);
  const [loadingRentalInvoices, setLoadingRentalInvoices] = useState(false);
  const [dateEntries, setDateEntries] = useState([]);

  // Mobile detection hook
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 640);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Generate new component key when switching between invoices
  useEffect(() => {
    // Only generate new keys when invoice ID actually changes
    const invoiceId = invoice?.id || 'new';
    const newFormKey = `form-${invoiceId}-${Date.now()}`;

    setFormKey(newFormKey);
  }, [invoice?.id]);

  // Initialize form with existing invoice data or linked quotation
  useEffect(() => {
    const initializeForm = () => {
      if (invoice) {
        // Use intelligent payment terms mapping for backward compatibility
        const paymentTermsMapping = mapPaymentTermsForForm(invoice.paymentTerms, invoice.customPaymentTerms);

        const formValues = {
          companyName: invoice.companyName,
          attn: invoice.attn,
          tel: invoice.tel,
          email: invoice.email,
          billingAddress: invoice.billingAddress,
          siteAddress: invoice.siteAddress,
          invoiceType: invoice.invoiceType,
          paymentTerms: paymentTermsMapping.paymentTerms,
          customPaymentTerms: paymentTermsMapping.customPaymentTerms,
          notes: invoice.notes,
          poNumber: invoice.poNumber,
          internalRemarks: invoice.internalRemarks
        };

        // Load original invoice reference for DAMAGED_ITEMS type
        if (invoice.invoiceType === INVOICE_TYPES.DAMAGED_ITEMS) {
          if (invoice.originalInvoiceNumber) {
            formValues.originalInvoiceNumber = invoice.originalInvoiceNumber;
          }
          if (invoice.originalInvoiceId) {
            formValues.originalInvoiceId = invoice.originalInvoiceId;
          }
          if (invoice.collectionDate) {
            const collectionDate = convertDateToAntdFormat(invoice.collectionDate);
            if (collectionDate) {
              formValues.collectionDate = collectionDate;
            }
          }
          // Load rental invoices to populate the dropdown
          loadRentalInvoices();
        }

        // Load unified dates if available
        if (invoice.dates && Array.isArray(invoice.dates) && invoice.dates.length > 0) {
          console.log('🔍 Loading unified dates from invoice:', invoice.dates);
          setDateEntries(invoice.dates);
        } else {
          // Create default date entry for existing invoices without unified dates
          console.log('🔍 No unified dates found, creating default entry');
          const defaultDates = [createEmptyDateEntry(invoice.invoiceType)];
          setDateEntries(defaultDates);
        }

        // Update states and form values
        form.setFieldsValue(formValues);
        setInvoiceType(invoice.invoiceType);

        // Regenerate auto descriptions for existing line items on load
        const processedLineItems = regenerateAutoDescriptionsOnLoad(invoice.lineItems || []);
        setLineItems(processedLineItems);

        setAdditionalServices(invoice.additionalServices || DEFAULT_ADDITIONAL_SERVICES);

        // Load existing discount and GST data
        if (invoice.discount) {
          setDiscount(invoice.discount);
        }
        if (invoice.gst) {
          setGst(invoice.gst);
        }
      } else if (linkedQuotation) {
        // Creating invoice from quotation - prepopulate data including dates
        const customerInfo = mapCustomerInfoFromQuotation(linkedQuotation);

        // Use intelligent payment terms mapping to transfer from quotation
        const paymentTermsMapping = mapPaymentTermsForForm(linkedQuotation.paymentTerms, linkedQuotation.customPaymentTerms);

        const formValues = {
          companyName: customerInfo.companyName,
          attn: customerInfo.attn,
          tel: customerInfo.tel,
          email: customerInfo.email,
          billingAddress: customerInfo.billingAddress,
          siteAddress: customerInfo.siteAddress,
          invoiceType: linkedQuotation.quotationType || INVOICE_TYPES.PURCHASE,
          paymentTerms: paymentTermsMapping.paymentTerms,
          customPaymentTerms: paymentTermsMapping.customPaymentTerms
        };

        form.setFieldsValue(formValues);
        setInvoiceType(linkedQuotation.quotationType || INVOICE_TYPES.PURCHASE);

        // Set line items from quotation with auto-generated descriptions
        if (linkedQuotation.lineItems && linkedQuotation.lineItems.length > 0) {
          const processedLineItems = regenerateAutoDescriptionsOnLoad(linkedQuotation.lineItems);
          setLineItems(processedLineItems);
        }

        // Set additional services from quotation
        if (linkedQuotation.additionalServices) {
          setAdditionalServices(linkedQuotation.additionalServices);
        }

        // Set discount from quotation
        if (linkedQuotation.discount) {
          setDiscount(linkedQuotation.discount);
        }

        // Set GST from quotation
        if (linkedQuotation.gst) {
          setGst(linkedQuotation.gst);
        }

        // Load dates from quotation if available, otherwise create default
        if (linkedQuotation.dates && Array.isArray(linkedQuotation.dates) && linkedQuotation.dates.length > 0) {
          console.log('🔍 Loading dates from linked quotation:', linkedQuotation.dates);
          setDateEntries(linkedQuotation.dates);
        } else {
          // Fallback to default date entry if quotation doesn't have unified dates
          console.log('🔍 No unified dates found in quotation, creating default entry');
          const defaultDates = [createEmptyDateEntry(linkedQuotation.quotationType || INVOICE_TYPES.PURCHASE)];
          setDateEntries(defaultDates);
        }
      } else {
        // New invoice
        form.setFieldsValue({
          invoiceType: INVOICE_TYPES.PURCHASE,
          paymentTerms: PAYMENT_TERMS.DEPOSIT_50
        });

        // Initialize with default entry for new invoices
        const defaultDates = [createEmptyDateEntry(INVOICE_TYPES.PURCHASE)];
        setDateEntries(defaultDates);
      }
    };

    const timeoutId = setTimeout(initializeForm, 0);
    return () => clearTimeout(timeoutId);
  }, [invoice, linkedQuotation, form]);

  // Separate useEffect to handle form updates without affecting dateEntries
  useEffect(() => {
    if (form) {
      // This effect only runs when form instance changes
      // It doesn't interfere with dateEntries initialization
    }
  }, [form]);

  const handleInvoiceTypeChange = (value) => {
    setInvoiceType(value);
    
    // Load rental invoices when DAMAGED_ITEMS is selected
    if (value === INVOICE_TYPES.DAMAGED_ITEMS) {
      loadRentalInvoices();
    }
  };

  // Load available rental invoices for damaged items reference
  const loadRentalInvoices = async () => {
    setLoadingRentalInvoices(true);
    try {
      const rentalInvoices = await getAvailableRentalInvoices();
      setAvailableRentalInvoices(rentalInvoices);
    } catch (error) {
      console.error('Error loading rental invoices:', error);
      message.error('Failed to load rental invoices');
    } finally {
      setLoadingRentalInvoices(false);
    }
  };

  const addLineItem = () => {
    const newId = Math.max(...lineItems.map(item => item.id)) + 1;
    const newLineItem = createEnhancedLineItem({ 
      id: newId, 
      description: '', 
      quantity: 1, 
      unitPrice: 0, 
      total: 0,
      workingHeight: undefined,
      width: undefined
    });
    setLineItems([...lineItems, newLineItem]);
  };

  const removeLineItem = useCallback((id) => {
    setLineItems(prevItems => {
      if (prevItems.length > 1) {
        return prevItems.filter(item => item.id !== id);
      }
      return prevItems;
    });
  }, []);

  const updateLineItem = useCallback((id, field, value) => {
    setLineItems(prevItems => prevItems.map(item => {
      if (item.id === id) {
        return updateLineItemWithSmartDescription(item, field, value);
      }
      return item;
    }));
  }, []);

  // Function to reset line item description to auto-generated version
  const resetLineItemDescription = useCallback((id) => {
    setLineItems(prevItems => prevItems.map(item => {
      if (item.id === id) {
        return resetToAutoDescription(item);
      }
      return item;
    }));
  }, []);

  // Function to accept auto-generated description update
  const acceptDescriptionUpdate = useCallback((id) => {
    setLineItems(prevItems => prevItems.map(item => {
      if (item.id === id) {
        return {
          ...item,
          description: item.autoDescription,
          isManuallyEdited: false,
          showUpdatePrompt: false
        };
      }
      return item;
    }));
  }, []);

  // Function to dismiss description update prompt
  const dismissDescriptionUpdate = useCallback((id) => {
    setLineItems(prevItems => prevItems.map(item => {
      if (item.id === id) {
        return {
          ...item,
          showUpdatePrompt: false
        };
      }
      return item;
    }));
  }, []);

  const toggleAdditionalService = useCallback((serviceKey) => {
    setAdditionalServices(prev => ({
      ...prev,
      [serviceKey]: {
        ...prev[serviceKey],
        selected: !prev[serviceKey]?.selected
      }
    }));
  }, []);

  const updateAdditionalServicePrice = useCallback((serviceKey, price) => {
    setAdditionalServices(prev => ({
      ...prev,
      [serviceKey]: {
        ...prev[serviceKey],
        price: price || 0
      }
    }));
  }, []);

  const calculateSubtotal = () => {
    return lineItems.reduce((sum, item) => sum + (item.total || 0), 0);
  };

  const calculateAdditionalServicesTotal = () => {
    return Object.values(additionalServices).reduce((sum, service) => 
      sum + (service.selected ? service.price : 0), 0
    );
  };

  // Removed calculateGrandTotal - not used

  const calculateDiscountAmount = () => {
    const subtotal = calculateSubtotal();
    if (discount.type === 'percentage') {
      return subtotal * (discount.value / 100);
    } else if (discount.type === 'fixed') {
      return discount.value;
    }
    return 0;
  };

  const calculateGSTAmount = () => {
    if (gst.percentage <= 0) return 0;
    const baseAmount = calculateSubtotal() - calculateDiscountAmount();
    return baseAmount * (gst.percentage / 100);
  };

  const calculateFinalTotal = () => {
    const subtotal = calculateSubtotal();
    const discountAmount = calculateDiscountAmount();
    const gstAmount = calculateGSTAmount();
    return subtotal - discountAmount + gstAmount;
  };

  // Handle multiple dates change
  const handleMultipleDatesChange = useCallback((newDateEntries) => {
    setDateEntries(newDateEntries);
  }, []);


  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      // Validate line items - at least one item must have a description
      const validLineItems = lineItems.filter(item => item.description && item.description.trim() !== '');
      if (validLineItems.length === 0) {
        message.error('Please add at least one service/product with a description');
        setLoading(false);
        return;
      }

      // Helper function to clean undefined values
      const cleanValue = (value) => {
        if (value === undefined || value === null) return '';
        if (typeof value === 'string' && value.trim() === '') return '';
        return value;
      };

      // Helper function to clean entire object of undefined values
      const cleanObject = (obj) => {
        if (!obj || typeof obj !== 'object') return obj;

        const cleaned = {};
        for (const [key, value] of Object.entries(obj)) {
          if (value !== undefined) {
            cleaned[key] = value;
          }
        }
        return cleaned;
      };

      // Helper function to sanitize line items for Firebase
      const sanitizeLineItems = (items) => {
        return items.map(item => {
          const sanitizedItem = {};
          
          // Define allowed fields and their default values
          const allowedFields = {
            id: null,
            description: '',
            quantity: 1,
            unitPrice: 0,
            total: 0,
            isManuallyEdited: false,
            autoDescription: '',
            showUpdatePrompt: false,
            services: {}
          };
          
          // Only include fields that have valid, non-undefined values
          Object.keys(allowedFields).forEach(key => {
            if (item[key] !== undefined && item[key] !== null) {
              sanitizedItem[key] = item[key];
            } else if (allowedFields[key] !== null) {
              // Use default value for required fields
              sanitizedItem[key] = allowedFields[key];
            }
          });
          
          // Only include width and workingHeight if they have valid values
          if (item.width !== undefined && item.width !== null && item.width !== '') {
            sanitizedItem.width = item.width;
          }
          
          if (item.workingHeight !== undefined && item.workingHeight !== null && item.workingHeight !== '') {
            sanitizedItem.workingHeight = item.workingHeight;
          }
          
          return sanitizedItem;
        });
      };

      // Use new unified date system - store dates array
      const invoiceData = cleanObject({
        // Customer information
        companyName: cleanValue(values.companyName),
        attn: cleanValue(values.attn),
        tel: cleanValue(values.tel),
        email: cleanValue(values.email),
        billingAddress: cleanValue(values.billingAddress),
        siteAddress: cleanValue(values.siteAddress),

        // Invoice details
        invoiceType: values.invoiceType,

        // NEW: Unified date system
        dates: dateEntries,

        // Payment and terms
        paymentTerms: values.paymentTerms,
        customPaymentTerms: values.paymentTerms === PAYMENT_TERMS.CUSTOM
          ? cleanValue(values.customPaymentTerms)
          : '',

        // PO Number
        poNumber: cleanValue(values.poNumber),

        // Discount information
        discount: discount || { type: 'none', value: 0 },
        discountAmount: calculateDiscountAmount(),

        // GST information
        gst: gst || { percentage: 0 },
        gstAmount: calculateGSTAmount(),

        // Line items and pricing
        lineItems: sanitizeLineItems(lineItems) || [],
        additionalServices: additionalServices || {},
        subtotal: calculateSubtotal(),
        additionalServicesTotal: calculateAdditionalServicesTotal(),
        grandTotal: calculateFinalTotal(),

        // Additional information (optional)
        notes: cleanValue(values.notes),
        internalRemarks: cleanValue(values.internalRemarks),
        customAdditionalServices: cleanValue(values.customAdditionalServices),

        // Link to quotation if applicable
        quotationId: linkedQuotation?.id || invoice?.quotationId || '',
        quotationNumber: linkedQuotation?.quotationNumber || invoice?.quotationNumber || ''
      });

      // Add DAMAGED_ITEMS specific fields
      if (values.invoiceType === INVOICE_TYPES.DAMAGED_ITEMS) {
        // Collection date
        if (values.collectionDate) {
          invoiceData.collectionDate = convertAntdDateToStorage(values.collectionDate) || '';
        }
        
        // Original invoice reference - support both manual entry and dropdown selection
        if (values.originalInvoiceNumber) {
          invoiceData.originalInvoiceNumber = cleanValue(values.originalInvoiceNumber);
        }
        
        // If originalInvoiceId is available (from dropdown selection), store it too
        if (values.originalInvoiceId) {
          invoiceData.originalInvoiceId = cleanValue(values.originalInvoiceId);
        }
      }

      console.log('Saving invoice with unified dates:', {
        dates: invoiceData.dates,
        invoiceType: invoiceData.invoiceType
      });

      let result;
      if (invoice) {
        // Update existing invoice
        result = await updateInvoice(invoice.id, invoiceData);
        if (result) {
          message.success('Invoice updated successfully');
          onSave({ id: invoice.id, ...invoiceData });
        }
      } else {
        // Create new invoice
        result = await createInvoice(invoiceData);
        message.success('Invoice created successfully');
        onSave(result);
      }
    } catch (error) {
      console.error('Error saving invoice:', error);
      message.error('Failed to save invoice. Please check all required fields.');
    } finally {
      setLoading(false);
    }
  };



  const lineItemColumns = [
    {
      title: '#',
      dataIndex: 'index',
      key: 'index',
      width: 50,
      render: (_, __, index) => index + 1
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (text, record) => (
        <div>
          <div style={{ position: 'relative' }}>
            <TextArea
              value={text}
              onChange={(e) => updateLineItem(record.id, 'description', e.target.value)}
              placeholder="Enter service description (multi-line supported)"
              autoSize={{ minRows: 2, maxRows: 4 }}
              style={{
                borderColor: record.isManuallyEdited ? '#1890ff' : undefined,
                backgroundColor: record.isManuallyEdited ? '#f6ffed' : undefined
              }}
            />
            {/* Visual indicator for auto-generated vs manual descriptions */}
            {!record.isManuallyEdited && record.autoDescription && (
              <div style={{
                position: 'absolute',
                top: 4,
                right: 4,
                fontSize: '10px',
                color: '#52c41a',
                backgroundColor: 'rgba(82, 196, 26, 0.1)',
                padding: '2px 4px',
                borderRadius: '2px',
                display: 'flex',
                alignItems: 'center',
                gap: '2px'
              }}>
                <SyncOutlined style={{ fontSize: '8px' }} />
                Auto
              </div>
            )}
          </div>

          {/* Update prompt for manually edited descriptions */}
          {record.showUpdatePrompt && (
            <div style={{
              marginTop: 4,
              padding: '6px 8px',
              backgroundColor: '#fff7e6',
              border: '1px solid #ffd591',
              borderRadius: '4px',
              fontSize: '12px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <InfoCircleOutlined style={{ color: '#fa8c16' }} />
                  <span>Update available: "{record.autoDescription}"</span>
                </div>
                <div>
                  <Button
                    size="small"
                    type="text"
                    icon={<CheckOutlined />}
                    onClick={() => acceptDescriptionUpdate(record.id)}
                    style={{ color: '#52c41a', padding: '0 4px' }}
                    title="Accept update"
                  />
                  <Button
                    size="small"
                    type="text"
                    icon={<CloseOutlined />}
                    onClick={() => dismissDescriptionUpdate(record.id)}
                    style={{ color: '#ff4d4f', padding: '0 4px' }}
                    title="Dismiss"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Helper function controls */}
          <div style={{ marginTop: 8, padding: '8px', backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
            <Space wrap>
              <Radio.Group
                size="small"
                value={record.width}
                onChange={(e) => updateLineItem(record.id, 'width', e.target.value)}
              >
                <Radio.Button value="single">Single</Radio.Button>
                <Radio.Button value="double">Double</Radio.Button>
              </Radio.Group>
              <InputNumber
                size="small"
                placeholder="Height"
                value={record.workingHeight}
                onChange={(value) => updateLineItem(record.id, 'workingHeight', value)}
                min={2}
                max={15}
                step={0.1}
                addonAfter="m"
                style={{ width: '100px' }}
              />
              {/* Reset to auto button */}
              {record.isManuallyEdited && record.autoDescription && (
                <Button
                  size="small"
                  type="text"
                  icon={<SyncOutlined />}
                  onClick={() => resetLineItemDescription(record.id)}
                  title="Reset to auto-generated description"
                  style={{ color: '#1890ff' }}
                >
                  Reset
                </Button>
              )}
            </Space>
            {/* Service checkboxes */}
            <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              <Checkbox
                size="small"
                checked={record.services?.transportation || false}
                onChange={(e) => updateLineItem(record.id, 'services', { 
                  ...record.services, 
                  transportation: e.target.checked 
                })}
                style={{ fontSize: '11px' }}
              >
                Transportation
              </Checkbox>
              <Checkbox
                size="small"
                checked={record.services?.assembly || false}
                onChange={(e) => updateLineItem(record.id, 'services', { 
                  ...record.services, 
                  assembly: e.target.checked 
                })}
                style={{ fontSize: '11px' }}
              >
                Assembly & dismantling
              </Checkbox>
              <Checkbox
                size="small"
                checked={record.services?.endorsement || false}
                onChange={(e) => updateLineItem(record.id, 'services', { 
                  ...record.services, 
                  endorsement: e.target.checked 
                })}
                style={{ fontSize: '11px' }}
              >
                Weekly inspection
              </Checkbox>
            </div>
          </div>
        </div>
      )
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      render: (text, record) => (
        <InputNumber
          min={1}
          value={text}
          onChange={(value) => updateLineItem(record.id, 'quantity', value)}
        />
      )
    },
    {
      title: 'Unit Price (SGD)',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      width: 150,
      render: (text, record) => (
        <InputNumber
          min={0}
          precision={2}
          value={text}
          onChange={(value) => updateLineItem(record.id, 'unitPrice', value)}
          formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
        />
      )
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      width: 150,
      render: (text) => `$${(text || 0).toFixed(2)}`
    },
    {
      title: 'Action',
      key: 'action',
      width: 80,
      render: (_, record) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => removeLineItem(record.id)}
          disabled={lineItems.length === 1}
        />
      )
    }
  ];

  return (
    <div className="admin-form-container" style={{ maxWidth: 1200, margin: '0 auto', padding: 20 }}>
      <Card>
        <div style={{ marginBottom: 24 }}>
          <Title level={3}>
            {invoice ? 'Edit Invoice' : 'Create New Invoice'}
          </Title>
          {invoice && (
            <Text type="secondary">
              Invoice Number: {invoice.invoiceNumber}
            </Text>
          )}
          {linkedQuotation && (
            <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
              Created from Quotation: {linkedQuotation.quotationNumber}
            </Text>
          )}
        </div>

        <Form
          key={formKey}
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            invoiceType: INVOICE_TYPES.PURCHASE,
            paymentTerms: PAYMENT_TERMS.DEPOSIT_50
          }}
        >
          {/* Customer Information Section */}
          <Card title="Customer Information" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Company Name"
                  name="companyName"
                  rules={[{ required: true, message: 'Please enter company name' }]}
                >
                  <Input placeholder="Enter company name" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Attention To"
                  name="attn"
                  rules={[{ required: true, message: 'Please enter contact person' }]}
                >
                  <Input placeholder="Enter contact person name" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Telephone"
                  name="tel"
                  rules={[{ required: true, message: 'Please enter telephone number' }]}
                >
                  <Input placeholder="Enter telephone number" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Email"
                  name="email"
                  rules={[
                    { type: 'email', message: 'Please enter a valid email address' }
                  ]}
                >
                  <Input placeholder="Enter email address (optional - will show as NA if not provided)" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Billing Address (Optional)"
                  name="billingAddress"
                >
                  <TextArea 
                    rows={2} 
                    placeholder="Enter billing address if different from site address (optional)" 
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  label="Site Address"
                  name="siteAddress"
                >
                  <TextArea 
                    rows={2} 
                    placeholder="Enter the site address where services will be provided (optional)" 
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* Invoice Details Section */}
          <Card title="Invoice Details" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Invoice Type"
                  name="invoiceType"
                  rules={[{ required: true, message: 'Please select invoice type' }]}
                >
                  <Select onChange={handleInvoiceTypeChange}>
                    {Object.entries(INVOICE_TYPE_LABELS).map(([key, label]) => (
                      <Option key={key} value={key}>{label}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="PO Number (Optional)"
                  name="poNumber"
                >
                  <Input placeholder="Enter PO number if available" />
                </Form.Item>
              </Col>
            </Row>
            
            {/* DAMAGED_ITEMS specific fields */}
            {invoiceType === INVOICE_TYPES.DAMAGED_ITEMS && (
              <>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      label="Collection Date"
                      name="collectionDate"
                      rules={[{ required: true, message: 'Please select the collection date' }]}
                      help="Select the date when the damaged items were discovered/collected"
                    >
                      <DatePicker 
                        style={{ width: '100%' }}
                        placeholder="Select collection date"
                        format="DD/MM/YYYY"
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label="Original Rental Invoice"
                      name="originalInvoiceNumber"
                      rules={[{ required: true, message: 'Please enter or select the original rental invoice' }]}
                      help="Enter invoice number manually or select from system invoices"
                    >
                      <AutoComplete
                        placeholder="Enter invoice number or select from dropdown"
                        loading={loadingRentalInvoices}
                        options={availableRentalInvoices.map(rentalInvoice => ({
                          value: rentalInvoice.invoiceNumber,
                          label: `${rentalInvoice.invoiceNumber} - ${rentalInvoice.companyName} (${moment(rentalInvoice.createdAt).format('DD/MM/YYYY')})`,
                          invoiceId: rentalInvoice.id
                        }))}
                        onSelect={(value, option) => {
                          // When selecting from dropdown, also store the invoice ID
                          form.setFieldsValue({ 
                            originalInvoiceNumber: value,
                            originalInvoiceId: option.invoiceId 
                          });
                        }}
                        filterOption={(inputValue, option) =>
                          option.label.toLowerCase().indexOf(inputValue.toLowerCase()) !== -1
                        }
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </>
            )}

            <Row gutter={16}>
              <Col span={24}>
                {/* Use UnifiedDateSection for all invoice types except DAMAGED_ITEMS */}
                {invoiceType !== INVOICE_TYPES.DAMAGED_ITEMS ? (
                  <UnifiedDateSection
                    documentType={invoiceType}
                    dates={dateEntries}
                    onDatesChange={handleMultipleDatesChange}
                  />
                ) : null}
              </Col>
            </Row>
            
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Payment Terms"
                  name="paymentTerms"
                  rules={[{ required: true, message: 'Please select payment terms' }]}
                >
                  <Select>
                    <Option value={PAYMENT_TERMS.DEPOSIT_50}>50% payment upon confirmation, balance on delivery / first day of rental</Option>
                    <Option value={PAYMENT_TERMS.FULL_PAYMENT}>100% payment is due</Option>
                    <Option value={PAYMENT_TERMS.CUSTOM}>Custom terms</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  noStyle
                  shouldUpdate={(prevValues, currentValues) =>
                    prevValues.paymentTerms !== currentValues.paymentTerms
                  }
                >
                  {({ getFieldValue }) =>
                    getFieldValue('paymentTerms') === PAYMENT_TERMS.CUSTOM ? (
                      <Form.Item
                        label="Custom Payment Terms"
                        name="customPaymentTerms"
                        rules={[{ required: true, message: 'Please enter custom payment terms' }]}
                      >
                        <TextArea rows={2} placeholder="Enter custom payment terms" />
                      </Form.Item>
                    ) : null
                  }
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* Discount Section */}
          <DiscountSection
            discount={discount}
            onDiscountChange={setDiscount}
            subtotal={calculateSubtotal()}
          />

          {/* GST Section */}
          <GSTSection
            gst={gst}
            onGSTChange={setGst}
            subtotalAfterDiscount={calculateSubtotal() - calculateDiscountAmount()}
          />

          {/* Line Items Section */}
          <Card 
            title="Services/Products" 
            style={{ marginBottom: 16 }}
            extra={
              <Button 
                type="dashed" 
                icon={<PlusOutlined />} 
                onClick={addLineItem}
              >
                Add Item
              </Button>
            }
          >
            {/* Conditional rendering: Mobile layout or Table */}
            {isMobile ? (
              <MobileLineItems 
                lineItems={lineItems}
                onUpdate={updateLineItem}
                onRemove={removeLineItem}
                onResetDescription={resetLineItemDescription}
                onAcceptUpdate={acceptDescriptionUpdate}
                onDismissUpdate={dismissDescriptionUpdate}
              />
            ) : (
              <Table
                dataSource={lineItems}
                columns={lineItemColumns}
                pagination={false}
                rowKey="id"
                size="small"
                className={isMobile ? 'mobile-hidden' : ''}
              />
            )}
            
            <div style={{ marginTop: 16, textAlign: 'right' }}>
              <div>
                <Text strong>Subtotal: ${calculateSubtotal().toFixed(2)}</Text>
              </div>
              {discount.type !== 'none' && discount.value > 0 && (
                <div style={{ marginTop: 4 }}>
                  <Text style={{ color: '#52c41a' }}>
                    Discount: -${calculateDiscountAmount().toFixed(2)}
                  </Text>
                </div>
              )}
              {gst.percentage > 0 && (
                <div style={{ marginTop: 4 }}>
                  <Text style={{ color: '#1890ff' }}>
                    GST ({gst.percentage}%): +${calculateGSTAmount().toFixed(2)}
                  </Text>
                </div>
              )}
              <div style={{ marginTop: 8, paddingTop: 4, borderTop: '1px solid #d9d9d9' }}>
                <Text strong style={{ fontSize: '16px' }}>
                  TOTAL (SGD): ${calculateFinalTotal().toFixed(2)}
                </Text>
              </div>
            </div>
          </Card>


          {/* Additional Services Section */}
          <AdditionalServicesSection
            additionalServices={additionalServices}
            onToggleService={toggleAdditionalService}
            onUpdateServicePrice={updateAdditionalServicePrice}
            customAdditionalServices={customAdditionalServices}
            onCustomAdditionalServicesChange={setCustomAdditionalServices}
            readOnly={true}
          />

          {/* Notes Section */}
          <Card title="Additional Notes" style={{ marginBottom: 16 }}>
            <Form.Item
              label="Notes"
              name="notes"
            >
              <TextArea
                rows={4}
                placeholder="Enter any additional notes or terms..."
              />
            </Form.Item>
          </Card>

          {/* Internal Remarks Section */}
          <Card 
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ color: '#1890ff' }}>🔒</span>
                <span>Internal Remarks (Admin Only)</span>
              </div>
            } 
            style={{ 
              marginBottom: 24,
              backgroundColor: '#f6ffed',
              border: '1px solid #b7eb8f'
            }}
          >
            <div style={{ 
              marginBottom: 8, 
              fontSize: '12px', 
              color: '#666',
              fontStyle: 'italic'
            }}>
              Private notes for internal reference only. This will not appear in PDFs or client communications.
            </div>
            <Form.Item
              label="Internal Remarks"
              name="internalRemarks"
            >
              <TextArea
                rows={3}
                placeholder="Private notes for internal reference (e.g., client requested clean scaffold, special delivery instructions, follow-up reminders, etc.)"
                style={{ backgroundColor: '#fff' }}
              />
            </Form.Item>
          </Card>

          {/* Action Buttons */}
          <div style={{ textAlign: 'center', marginTop: 24 }}>
            <Space size="middle">
              <Button onClick={onCancel}>
                Cancel
              </Button>
              <Button 
                type="primary" 
                icon={<SaveOutlined />} 
                htmlType="submit" 
                loading={loading}
              >
                Save Invoice
              </Button>
            </Space>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default InvoiceForm;
